import { IonTabBar, IonTabButton, useIonRouter } from '@ionic/react';
import { Permission } from '@up2stay/up2promo-api-client';
import { cn } from '@up2stay/up2promo-ui';
import { Avatar, AvatarFallback, AvatarImage } from '@up2stay/up2promo-ui';
import { Home, QrCode, UserCircle, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { GUIDANCE_TARGET } from '@/core/guidance/constants';
import { useMoreTabStackReset } from '@/core/hooks/use-more-tab-stack-reset';
import { useNavAttentionCounts } from '@/core/hooks/use-nav-attention-counts';
import { useNotificationHubBadgeCount } from '@/core/hooks/use-notifications';
import { usePermissions } from '@/core/hooks/use-permissions';
import {
  SHELL_TAB_BAR_EXPAND_EVENT,
  useShellTabBarCompact,
} from '@/core/hooks/use-shell-tab-bar-compact';
import { useShellTabBarTouchGlow } from '@/core/hooks/use-shell-tab-bar-touch-glow';
import { useWorkspaceBootstrapStatus } from '@/core/hooks/use-workspace-bootstrap';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import { scrollActiveShellPageToTop } from '@/core/lib/active-shell-scroll';
import { triggerTapHaptic } from '@/core/lib/dnd-haptics';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import {
  isNavigationLeaveBlocked,
  runWithNavigationLeaveGate,
} from '@/core/lib/navigation-leave-gate';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { canonicalizeShellPathname } from '@/core/router/app-paths';
import {
  type AppTab,
  getTabRootPath,
  resolveTabForPath,
  shouldScrollActiveTabToTop,
  TAB_HOME,
  TAB_LEADS,
  TAB_MORE,
  TAB_PROFILES,
  TAB_ROOTS,
} from '@/core/router/app-routes';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import { syncShellTabBarChromeDataset } from '@/core/utils/shell-chrome';

import { hasMoreTabOpsAttention } from './nav-attention';
import { QRCodeDrawer } from './qr-code-drawer';

type IonTabButtonClickDetail = {
  tab?: string;
  href?: string;
  selected: boolean;
};

/**
 * iv-ac style: {@link IonTabButton} + `href` inside {@link IonTabs}.
 */
export function ShellMobileTabBar() {
  const dict = useDictionary();
  const location = useLocation();
  const tabBarHostRef = useRef<HTMLDivElement>(null);
  const [qrDrawerOpen, setQrDrawerOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const currentWorkspace = useCurrentWorkspace();
  const { badgeCount } = useNotificationHubBadgeCount();
  const attentionCounts = useNavAttentionCounts();
  const showLeadsDot = attentionCounts.leads > 0;
  const showMoreDot = badgeCount > 0 || hasMoreTabOpsAttention(attentionCounts);
  const ionRouter = useIonRouter();
  const { workspaces, isError } = useWorkspaces();
  const bootstrapStatus = useWorkspaceBootstrapStatus();
  const { can } = usePermissions();
  /**
   * Tab *buttons* follow the role; the matching `Route`s stay mounted in
   * `app-shell-tabs-outlet` so Ionic tab stacks keep working (a removed route
   * breaks history for the whole outlet). Home and More are always reachable.
   */
  const showProfilesTab = can(Permission.PROFILES_READ);
  const showLeadsTab = can(Permission.LEADS_READ);
  // Prefer RR pathname so chrome matches the mounted page on first paint
  // (Ionic routeInfo can lag one frame after push — same as PageHeader).
  const pathname = canonicalizeShellPathname(
    location.pathname || ionRouter.routeInfo?.pathname || '',
  );
  const currentTab = resolveTabForPath(pathname);
  const isMoreTabActive = currentTab === TAB_MORE;
  /**
   * Main tab chrome only when bootstrap is ready (or error). Empty/loading/
   * needs-onboarding stay on the placeholder bar — create-first lives on
   * `/onboarding`, never as a Cap body portal (DEV-470).
   */
  const showMainTabBar =
    bootstrapStatus === 'ready' ||
    (bootstrapStatus === 'error' &&
      Boolean(currentWorkspace || workspaces.length > 0 || isError));
  /** Cap: main bar always painted once bootstrap is ready (all shell routes). */
  const tabBarExposed = showMainTabBar;

  useEffect(() => {
    syncShellTabBarChromeDataset(tabBarExposed);
  }, [tabBarExposed]);

  useEffect(() => {
    return () => {
      syncShellTabBarChromeDataset(false);
    };
  }, []);

  const scrollActiveTabToTop = useCallback(() => {
    void scrollActiveShellPageToTop();
    tabBarHostRef.current?.dispatchEvent(new Event(SHELL_TAB_BAR_EXPAND_EVENT));
  }, []);

  const onScrubActivate = useCallback(
    (button: HTMLElement) => {
      if (button.classList.contains('shell-mobile-tab-bar__qr-tab')) {
        void triggerTapHaptic();
        setQrDrawerOpen(true);
        return;
      }

      const href = button.getAttribute('href');
      if (!href) {
        return;
      }

      void triggerTapHaptic();

      const tab = button.getAttribute('tab') as AppTab | null;
      if (
        tab &&
        shouldScrollActiveTabToTop({
          clickedTab: tab,
          currentTab,
          pathname,
        })
      ) {
        scrollActiveTabToTop();
        return;
      }

      // Tab switch — no page animation; Ionic tab stacks own the transition.
      runWithNavigationLeaveGate(() => {
        ionRouter.push(href, 'none', 'push');
      });
    },
    [currentTab, ionRouter, pathname, scrollActiveTabToTop],
  );

  useShellTabBarCompact(
    isCapacitorNative() && showMainTabBar && tabBarExposed,
    tabBarHostRef,
  );
  useShellTabBarTouchGlow(
    isCapacitorNative() && showMainTabBar && tabBarExposed,
    tabBarHostRef,
    {
      onActivate: onScrubActivate,
    },
  );

  const tabButtonClass = (tab: AppTab) =>
    cn(currentTab === tab && 'shell-mobile-tab-bar__tab--active');

  const iconClass = (tab: AppTab) =>
    cn(
      'shell-mobile-tab-bar__icon',
      currentTab === tab
        ? 'shell-mobile-tab-bar__icon--active'
        : 'shell-mobile-tab-bar__icon--inactive',
    );

  const iconStrokeWidth = (tab: AppTab) => (currentTab === tab ? 2.5 : 2);

  useMoreTabStackReset(tabBarHostRef);

  useEffect(() => {
    const host = tabBarHostRef.current;
    if (!host) {
      return;
    }

    const tabsHost = host.closest('ion-tabs') ?? host;

    const onTabButtonClick = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const tabButton = target.closest('ion-tab-button');
      if (!tabButton || !host.contains(tabButton)) {
        return;
      }

      const ionEvent = event as CustomEvent<IonTabButtonClickDetail>;
      const tab = ionEvent.detail?.tab as AppTab | undefined;
      if (!tab) {
        return;
      }

      void triggerTapHaptic();

      if (
        shouldScrollActiveTabToTop({
          clickedTab: tab,
          currentTab,
          pathname,
        })
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        scrollActiveTabToTop();
        return;
      }

      // IonTabButton uses href (bypasses navigateShell). When a leave gate is
      // active, cancel the default tab hop and resume via the gate's proceed.
      if (!isNavigationLeaveBlocked()) {
        return;
      }

      event.preventDefault();
      const href =
        ionEvent.detail?.href ??
        tabButton.getAttribute('href') ??
        TAB_ROOTS[tab];
      if (!href) {
        return;
      }

      runWithNavigationLeaveGate(() => {
        ionRouter.push(href, 'root', 'replace');
      });
    };

    const onQrClick = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const qrTab = target.closest('.shell-mobile-tab-bar__qr-tab');
      if (!qrTab || !host.contains(qrTab)) {
        return;
      }

      void triggerTapHaptic();
      event.preventDefault();
      event.stopPropagation();
      setQrDrawerOpen(true);
    };

    tabsHost.addEventListener('ionTabButtonClick', onTabButtonClick, true);
    host.addEventListener('click', onQrClick, true);

    return () => {
      tabsHost.removeEventListener('ionTabButtonClick', onTabButtonClick, true);
      host.removeEventListener('click', onQrClick, true);
    };
  }, [currentTab, ionRouter, pathname, scrollActiveTabToTop]);

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const avatarSource = useMemo(() => {
    if (currentWorkspace) {
      const photoPath = currentWorkspace.photo?.path;
      return {
        photo:
          photoPath && photoPath.trim()
            ? getAbsoluteFileUrl(photoPath)
            : undefined,
        name: currentWorkspace.name || 'Workspace',
        initials: getInitials(currentWorkspace.name || 'ORG'),
        alt: currentWorkspace.name || dict.workspace?.workspace || 'Workspace',
      };
    }
    const userPhotoPath = user?.photo?.path;
    return {
      photo:
        userPhotoPath && userPhotoPath.trim()
          ? getAbsoluteFileUrl(userPhotoPath)
          : undefined,
      name: user
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
          user.email ||
          'User'
        : 'User',
      initials: user
        ? getInitials(
            `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
              user.email ||
              'User',
          )
        : 'U',
      alt: user
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
          user.email ||
          'User'
        : 'User',
    };
  }, [currentWorkspace, dict.workspace?.workspace, user]);

  // Placeholder IonTabBar only — zero-workspace CTA is `/onboarding` via
  // WorkspaceBootstrapGate. Never render create-first here (body portals
  // escaped the previous overlay and flashed on Cap cold launch).
  if (!showMainTabBar) {
    return (
      <div
        slot="bottom"
        className="shell-mobile-tab-bar-host shell-mobile-tab-bar-host--empty shell-mobile-tab-bar-host--collapsed"
        aria-hidden
      >
        <IonTabBar className="shell-mobile-tab-bar">
          <IonTabButton tab={TAB_HOME} href={TAB_ROOTS[TAB_HOME]} disabled />
        </IonTabBar>
      </div>
    );
  }

  return (
    <>
      {/*
        `slot="bottom"` + fixed: lives outside `.tabs-inner` so full-bleed
        `.ion-page` layers never paint over the bar. Always visible on Cap.
      */}
      <div
        ref={tabBarHostRef}
        slot="bottom"
        className="shell-mobile-tab-bar-host"
      >
        {/*
          Scale lives on an inner layer so WebKit transform clipping cannot
          cut the protruding QR mark (sibling below).
        */}
        <div className="shell-mobile-tab-bar-scale">
          <div
            className="liquid-glass shell-mobile-tab-bar-glass shell-mobile-tab-bar-glass-bg"
            aria-hidden
          />
          <IonTabBar className="shell-mobile-tab-bar">
            <IonTabButton
              tab={TAB_HOME}
              href={TAB_ROOTS[TAB_HOME]}
              aria-label={dict.nav?.home ?? 'Home'}
              aria-current={currentTab === TAB_HOME ? 'page' : undefined}
              className={tabButtonClass(TAB_HOME)}
              data-guidance-target={GUIDANCE_TARGET.home}
            >
              <span className="shell-mobile-tab-bar__icon-slot">
                <Home
                  className={iconClass(TAB_HOME)}
                  strokeWidth={iconStrokeWidth(TAB_HOME)}
                />
              </span>
            </IonTabButton>
            {showProfilesTab ? (
              <IonTabButton
                tab={TAB_PROFILES}
                href={TAB_ROOTS[TAB_PROFILES]}
                aria-label={dict.nav?.profiles ?? 'Profiles'}
                aria-current={currentTab === TAB_PROFILES ? 'page' : undefined}
                className={tabButtonClass(TAB_PROFILES)}
                data-guidance-target={GUIDANCE_TARGET.profiles}
              >
                <span className="shell-mobile-tab-bar__icon-slot">
                  <UserCircle
                    className={iconClass(TAB_PROFILES)}
                    strokeWidth={iconStrokeWidth(TAB_PROFILES)}
                  />
                </span>
              </IonTabButton>
            ) : null}
            <IonTabButton
              className="shell-mobile-tab-bar__qr-tab"
              aria-label={dict.nav.qrCodeActions}
            >
              {/* Hit target only — visual mark is outside the scaled layer. */}
              <span className="shell-mobile-tab-bar__qr-hit" aria-hidden />
            </IonTabButton>
            {showLeadsTab ? (
              <IonTabButton
                tab={TAB_LEADS}
                href={TAB_ROOTS[TAB_LEADS]}
                aria-label={
                  showLeadsDot
                    ? `${dict.nav?.leads ?? 'Leads'}, ${dict.nav?.attentionTabLeads ?? 'New leads pending'}`
                    : (dict.nav?.leads ?? 'Leads')
                }
                aria-current={currentTab === TAB_LEADS ? 'page' : undefined}
                className={tabButtonClass(TAB_LEADS)}
                data-guidance-target={GUIDANCE_TARGET.leads}
              >
                <span className="shell-mobile-tab-bar__icon-slot relative">
                  <Users
                    className={iconClass(TAB_LEADS)}
                    strokeWidth={iconStrokeWidth(TAB_LEADS)}
                  />
                  {showLeadsDot ? (
                    <span
                      className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary ring-2 ring-background"
                      aria-hidden
                    />
                  ) : null}
                </span>
              </IonTabButton>
            ) : null}
            <IonTabButton
              tab={TAB_MORE}
              href={getTabRootPath(TAB_MORE)}
              aria-label={
                showMoreDot
                  ? `${dict.nav?.accountWorkspaces ?? 'More'}, ${dict.nav?.attentionTabMore ?? 'Items need attention'}`
                  : (dict.nav?.accountWorkspaces ?? 'More')
              }
              aria-current={currentTab === TAB_MORE ? 'page' : undefined}
              className={tabButtonClass(TAB_MORE)}
              data-guidance-target={GUIDANCE_TARGET.more}
            >
              <div className="relative">
                <Avatar
                  key={`${currentWorkspace?.id || user?.id}-${avatarSource.photo || 'no-photo'}`}
                  className={cn(
                    'shell-mobile-tab-bar__avatar',
                    isMoreTabActive
                      ? 'shell-mobile-tab-bar__avatar--active'
                      : 'shell-mobile-tab-bar__avatar--inactive',
                  )}
                >
                  {avatarSource.photo ? (
                    <AvatarImage
                      src={avatarSource.photo}
                      alt={avatarSource.alt}
                      draggable={false}
                    />
                  ) : null}
                  <AvatarFallback className="text-sm font-medium bg-primary/10 text-primary">
                    {avatarSource.initials}
                  </AvatarFallback>
                </Avatar>
                {showMoreDot ? (
                  <span
                    className={cn(
                      'absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-background',
                      attentionCounts.reviews > 0
                        ? 'bg-destructive'
                        : 'bg-primary',
                    )}
                    aria-hidden
                  />
                ) : null}
              </div>
            </IonTabButton>
          </IonTabBar>
        </div>
        <span className="shell-mobile-tab-bar__qr-mark" aria-hidden>
          <QrCode className="shell-mobile-tab-bar__qr-icon" strokeWidth={2.5} />
        </span>
      </div>
      <QRCodeDrawer open={qrDrawerOpen} onOpenChange={setQrDrawerOpen} />
    </>
  );
}
